import React, { useState } from "react";
import { Popover, Switch, Tabs, Tag, Tooltip, Typography } from "antd";
import {
  BgColorsOutlined,
  CheckOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import {
  COLOR_THEMES,
  DEFAULT_THEME,
  loadCompanyTheme,
  saveCompanyTheme,
  themeFromPrimary,
  type ColorThemePreset,
  type EmailTheme,
} from "./data/colorThemes.data";

const { Text } = Typography;

interface Props {
  value: EmailTheme;
  onChange: (theme: EmailTheme) => void;
}

interface AdvancedField {
  key: keyof EmailTheme;
  label: string;
  hint: string;
  group: "Brand" | "Background" | "Text" | "Sections";
}

const ADVANCED_FIELDS: AdvancedField[] = [
  { key: "primary", label: "Brand / Accent", hint: "Top bar, eyebrow, buttons, table accents", group: "Brand" },
  { key: "linkColor", label: "Link Color", hint: "Hyperlinks inside the email body", group: "Brand" },

  { key: "canvasBg", label: "Hero Background", hint: "Background of the title section", group: "Background" },
  { key: "bodyBg", label: "Body Background", hint: "Background of the main message area", group: "Background" },

  { key: "bodyText", label: "Body Text", hint: "Default paragraph color", group: "Text" },
];

const GROUPS = ["Brand", "Background", "Text"] as const;

const ColorThemePicker: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [companyDefault, setCompanyDefault] = useState<string | null>(
    loadCompanyTheme(),
  );

  const applyPreset = (preset: ColorThemePreset) => {
    onChange({ ...preset });
  };

  const matched = COLOR_THEMES.find(
    (t) => t.primary.toLowerCase() === value.primary.toLowerCase(),
  );

  const handleSaveAsCompanyDefault = (checked: boolean) => {
    if (checked && matched) {
      saveCompanyTheme(matched.id);
      setCompanyDefault(matched.id);
    } else {
      saveCompanyTheme("");
      setCompanyDefault(null);
    }
  };

  const handleAdvancedChange = (key: keyof EmailTheme, hex: string) => {
    onChange({ ...value, [key]: hex });
  };

  const resetToDefault = () => {
    onChange({ ...DEFAULT_THEME, ...themeFromPrimary(DEFAULT_THEME.primary) });
  };

  const presetsTab = (
    <div style={{ width: 400 }}>
      <div
        style={{
          fontSize: 11,
          color: "#9ca3af",
          marginBottom: 10,
        }}
      >
        Each preset sets background, text, accent and footer together.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
          marginBottom: 12,
          maxHeight: 360,
          overflowY: "auto",
          paddingRight: 4,
        }}
      >
        {COLOR_THEMES.map((theme) => {
          const isActive = matched?.id === theme.id;
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => applyPreset(theme)}
              style={{
                padding: 0,
                borderRadius: 10,
                overflow: "hidden",
                border: isActive
                  ? `2px solid ${theme.primary}`
                  : "1px solid #e5e7eb",
                cursor: "pointer",
                background: theme.canvasBg,
                transition: "all 0.15s",
                boxShadow: isActive
                  ? `0 0 0 3px ${theme.primary}33, 0 2px 6px rgba(0,0,0,0.08)`
                  : "0 1px 2px rgba(0,0,0,0.05)",
                textAlign: "left",
              }}
            >
              {/* Mini-preview email */}
              <div
                style={{
                  background: theme.canvasBg,
                  padding: 6,
                }}
              >
                <div
                  style={{
                    background: theme.bodyBg,
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: 22,
                      background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primary}cc 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0 6px",
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 5,
                        background: theme.headerText,
                        opacity: 0.85,
                        borderRadius: 2,
                      }}
                    />
                    {isActive && (
                      <CheckOutlined
                        style={{
                          color: theme.headerText,
                          fontSize: 9,
                        }}
                      />
                    )}
                  </div>
                  <div style={{ padding: "8px 8px 6px" }}>
                    <div
                      style={{
                        width: "60%",
                        height: 5,
                        background: theme.bodyText,
                        opacity: 0.85,
                        borderRadius: 2,
                        marginBottom: 4,
                      }}
                    />
                    <div
                      style={{
                        width: "85%",
                        height: 4,
                        background: theme.bodyText,
                        opacity: 0.5,
                        borderRadius: 2,
                        marginBottom: 4,
                      }}
                    />
                    <div
                      style={{
                        background: theme.sectionBg,
                        height: 14,
                        borderRadius: 3,
                        marginBottom: 4,
                      }}
                    />
                    <div
                      style={{
                        width: "30%",
                        height: 4,
                        background: theme.linkColor,
                        borderRadius: 2,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      background: theme.footerBg,
                      height: 10,
                      borderTop: `1px solid ${theme.footerText}22`,
                    }}
                  />
                </div>
              </div>
              {/* Label row */}
              <div
                style={{
                  padding: "6px 8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#fff",
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 11.5 }}>
                    {theme.name}
                  </div>
                  {theme.description && (
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>
                      {theme.description}
                    </div>
                  )}
                </div>
                <Tag
                  style={{
                    margin: 0,
                    fontSize: 9,
                    padding: "0 4px",
                    background: theme.mode === "dark" ? "#111827" : "#f3f4f6",
                    color: theme.mode === "dark" ? "#fff" : "#374151",
                    border: "none",
                  }}
                >
                  {theme.mode}
                </Tag>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          borderTop: "1px solid #f0f0f0",
          paddingTop: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Text style={{ fontSize: 11, fontWeight: 500 }}>
            Use as company default
          </Text>
          <div style={{ fontSize: 10, color: "#9ca3af" }}>
            New templates start with this theme.
          </div>
        </div>
        <Switch
          size="small"
          checked={Boolean(matched && matched.id === companyDefault)}
          disabled={!matched}
          onChange={handleSaveAsCompanyDefault}
        />
      </div>
    </div>
  );

  const advancedTab = (
    <div style={{ width: 400, maxHeight: 380, overflowY: "auto" }}>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>
        Fine-tune every color used in the template.
      </div>

      {GROUPS.map((group) => {
        const groupFields = ADVANCED_FIELDS.filter((f) => f.group === group);
        return (
          <div key={group} style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 6,
                paddingBottom: 4,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {group}
            </div>
            {groupFields.map((field) => (
              <Tooltip
                key={field.key as string}
                title={field.hint}
                placement="left"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "4px 0",
                  }}
                >
                  <input
                    type="color"
                    value={value[field.key]}
                    onChange={(e) =>
                      handleAdvancedChange(field.key, e.target.value)
                    }
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: "1px solid #e5e7eb",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text style={{ fontSize: 11.5, fontWeight: 500 }}>
                      {field.label}
                    </Text>
                  </div>
                  <Text
                    code
                    style={{ fontSize: 10, textTransform: "uppercase" }}
                  >
                    {value[field.key]}
                  </Text>
                </div>
              </Tooltip>
            ))}
          </div>
        );
      })}

      <div
        style={{
          textAlign: "right",
          marginTop: 4,
          paddingTop: 8,
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <button
          type="button"
          onClick={resetToDefault}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: "#1677ff",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px 8px",
          }}
        >
          <ReloadOutlined /> Reset to default
        </button>
      </div>
    </div>
  );

  const content = (
    <div>
      <div
        style={{
          padding: "0 0 8px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 8,
        }}
      >
        <Text strong style={{ fontSize: 12 }}>
          Email theme
        </Text>
        <div style={{ fontSize: 10, color: "#9ca3af" }}>
          Changes apply live to the preview.
        </div>
      </div>
      <Tabs
        size="small"
        items={[
          { key: "presets", label: "Presets", children: presetsTab },
          { key: "advanced", label: "Customize", children: advancedTab },
        ]}
      />
      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px solid #f0f0f0",
          textAlign: "right",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            fontWeight: 600,
            color: "#fff",
            background: value.primary,
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            padding: "6px 14px",
          }}
        >
          <CheckOutlined /> Done
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayStyle={{ zIndex: 1100 }}
    >
      <button
        type="button"
        title="Customize email theme"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 12px",
          borderRadius: 16,
          background: "rgba(255,255,255,0.95)",
          border: "1px solid rgba(255,255,255,0.6)",
          color: "#111827",
          fontSize: 11.5,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${value.primary} 0%, ${value.primary}cc 100%)`,
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        />
        <BgColorsOutlined style={{ fontSize: 11 }} />
        <span>{matched ? matched.name : "Custom theme"}</span>
      </button>
    </Popover>
  );
};

export default ColorThemePicker;
